/**
 * Staff Authentication Route Guard
 * Protects admin routes that require federated staff authentication from Lytning POS
 */

import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useStaffAuthStore } from '@/stores/useStaffAuthStore'

/**
 * Route meta for staff-protected routes
 */
export interface StaffRouteMeta {
  /** Required permissions to access this route */
  staffPermissions?: string[]
  /** Whether to require staff authentication (defaults to true for staff routes) */
  requiresStaffAuth?: boolean
}

/**
 * Extract store slug from route params
 */
function extractStoreSlug(to: RouteLocationNormalized): string | undefined {
  return to.params.slug as string | undefined
}

function extractBootstrapToken(to: RouteLocationNormalized): string | null {
  const token = to.query.staff_token
  if (typeof token === 'string' && token.trim().length > 0) {
    return token.trim()
  }
  return null
}

/**
 * Staff authentication guard for admin routes
 * Checks session validity, store access, and permissions
 */
export async function staffAuthGuard(
  to: RouteLocationNormalized,
  _from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  const staffAuth = useStaffAuthStore()
  const errorRouteName = 'AdminAuthError'

  // Extract store slug from route params
  const routeSlug = extractStoreSlug(to)
  const bootstrapToken = extractBootstrapToken(to)

  if (bootstrapToken) {
    const tokenError = await staffAuth.initialize(bootstrapToken)
    if (tokenError) {
      console.warn('[StaffAuthGuard] Bootstrap token exchange failed:', tokenError)
      staffAuth.reset()
      next({
        name: errorRouteName,
        query: { error: tokenError }
      })
      return
    }

    const cleanedQuery = { ...to.query }
    delete cleanedQuery.staff_token
    next({
      path: to.path,
      query: cleanedQuery,
      replace: true
    })
    return
  }

  // Initialize session if not already done
  if (!staffAuth.isInitialized) {
    const error = await staffAuth.initialize()
    if (error) {
      console.warn('[StaffAuthGuard] Session initialization failed:', error)
      next({
        name: errorRouteName,
        query: { error }
      })
      return
    }
  }

  // Check if authenticated
  if (!staffAuth.isAuthenticated) {
    console.warn('[StaffAuthGuard] No valid session')
    next({
      name: errorRouteName,
      query: { error: 'NO_SESSION' }
    })
    return
  }

  // Check store slug match (staff can only access their assigned store)
  if (routeSlug && !staffAuth.canAccessStore(routeSlug)) {
    console.warn('[StaffAuthGuard] Store mismatch:', {
      route: routeSlug,
      session: staffAuth.currentSlug
    })
    next({
      name: errorRouteName,
      query: { error: 'STORE_MISMATCH' }
    })
    return
  }

  // Check permissions if specified in route meta
  const requiredPermissions = to.meta.staffPermissions as string[] | undefined
  if (requiredPermissions?.length) {
    const hasPermission = requiredPermissions.some(perm => staffAuth.hasPermission(perm))
    if (!hasPermission) {
      console.warn('[StaffAuthGuard] Permission denied:', {
        required: requiredPermissions,
        has: staffAuth.staffPermissions
      })
      next({
        name: errorRouteName,
        query: { error: 'PERMISSION_DENIED' }
      })
      return
    }
  }

  // All checks passed
  next()
}

/**
 * Simple guard that just checks if staff is authenticated
 * Used for routes that don't require specific permissions
 */
export async function requireStaffAuth(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
): Promise<void> {
  return staffAuthGuard(to, from, next)
}

export default staffAuthGuard
